// @flow

import * as React from 'react';
import { Button, Box, ThemeContext } from 'grommet';
import { getSidePanel, openPanel, closePanel } from 'esm-web-pro/redux/side-panel';
import { useDispatch, useSelector } from 'react-redux';
import QuickViewIcon from 'esm-web-pro/components/icons/QuickViewIcon';
import { Route } from 'react-router-dom';
import { useRouteMatch, useHistory, useLocation } from 'react-router';
import { get } from 'lodash';
import usePrevious from 'esm-web-pro/hooks/usePrevious';
import { APIContext } from 'speed-js-react';

type Props = {
    accessor: string,
    component: React.Node,
    row: Object,
    resourceName: string,
    parentAccessor?: string,
    queryparams?: Object,
    panelId: string,
    getTitle?: (obj: Object) => string,
    reload: Function,
    getResourceArguments?: (rowOriginal: Object) => Array<any>,
    resourceArguments?: Array<any>,
    match: Object,
    history: Object,
    location: Object,
    extraProps?: Object,
    uuid?: string | null,
    icon?: React.Node,
}

export const QuickViewContext: React.Context<Object> = React.createContext(false);

/**
 * Quick view component driven by the route. The view is opened when the url is changed and it matches tha path
 * of thew view. Then the object is fetched and shown. The is closed when the id of its panel is no longer in redux
 * state. Then the url and local state are cleared.
 * @param {Props} props Component props
 * @return {*} Returns react node
 * @constructor
 */
const QuickViewComponent = (props: Props) => {
    const {
        accessor, component, row, resourceName, queryparams, panelId, reload,
        parentAccessor, extraProps, uuid, getTitle, getResourceArguments, icon,
    } = props;
    let { resourceArguments } = props;

    const match = useRouteMatch();
    const history = useHistory();
    const location = useLocation();

    const dispatch = useDispatch();
    const { apiClient } = React.useContext(APIContext);

    const getObject = React.useCallback((id) => (
        get(apiClient.resources, resourceName).get(id, queryparams)
    ), [apiClient], resourceName, queryparams);

    const getNestedObject = React.useCallback((args, id) => (
        get(apiClient.resources, resourceName).get(...args, id, queryparams)
    ), [apiClient, resourceName, queryparams]);

    const [object, setObject] = React.useState(null);
    const theme = React.useContext(ThemeContext);

    const QuickView = component;

    const objectId = get(row._original, accessor) || uuid;

    if (getResourceArguments) {
        resourceArguments = [...getResourceArguments(row._original), ...resourceArguments];
    }

    if (parentAccessor) {
        const parentId = get(row._original, parentAccessor);
        if (resourceArguments) {
            resourceArguments = [parentId, ...resourceArguments];
        } else {
            resourceArguments = [parentId];
        }
    }

    const panel = useSelector((state) => getSidePanel(state, panelId + objectId));
    const prevPanel = usePrevious(panel);

    const path = `${match.url}/quick-view/:quickObjectId`;

    const handleReload = React.useCallback((shouldFetch = true) => {
        if (shouldFetch && shouldFetch !== 'delete') {
            if (resourceArguments) {
                getNestedObject(resourceArguments, objectId).then((res) => {
                    setObject(res);
                }).catch(() => {
                    setObject(null);
                });
            } else {
                getObject(objectId).then((res) => {
                    setObject(res);
                }).catch(() => {
                    setObject(null);
                });
            }
        }
        reload();
    }, [reload, getNestedObject, getObject, resourceArguments, objectId]);

    React.useEffect(() => {
        // Fetch the object when the url matches the path of this quick view
        if (!panel) {
            if (!object && location.pathname === path.replace(':quickObjectId', objectId)) {
                if (resourceArguments) {
                    let args = resourceArguments;
                    if (!Array.isArray(args)) {
                        args = args(row);
                    }
                    getNestedObject(args, objectId).then((res) => {
                        setObject(res);
                        dispatch(openPanel(panelId + objectId, getTitle ? getTitle(res) : ''));
                    });
                } else {
                    getObject(objectId).then((res) => {
                        setObject(res);
                        dispatch(openPanel(panelId + objectId, getTitle ? getTitle(res) : ''));
                    });
                }
            } else if (object && prevPanel) {
                // This will fetch the correct id of the object as it does not depend on the table
                // that is currently rendered
                const objectAccessorId = get(object, accessor) || objectId;
                dispatch(closePanel(panelId + objectAccessorId, true));
                history.push(location.pathname.replace(`/quick-view/${objectAccessorId}`, ''));
                setObject(null);
            }
        }
    }, [
        location.pathname, path, dispatch, getNestedObject, getObject, getTitle, object, objectId, panelId,
        queryparams, resourceArguments, resourceName, row, panel, prevPanel, history, accessor,
    ]);

    return (
        <QuickViewContext.Provider value>
            <Box>
                <Button
                    icon={icon ?? <QuickViewIcon />}
                    plain={false}
                    href={path.replace(':quickObjectId', objectId)}
                    onClick={(e) => {
                        e.preventDefault();
                        history.push(path.replace(':quickObjectId', objectId));
                    }}
                />
                <Route
                    path={path}
                    render={() => (object ? (
                        <ThemeContext.Extend value={theme.quickView}>
                            <QuickView
                                object={object}
                                panelId={panelId + objectId}
                                reload={handleReload}
                                resourceArguments={resourceArguments}
                                extraProps={extraProps}
                                {...extraProps}
                            />
                        </ThemeContext.Extend>
                    ) : null
                    )}
                />
            </Box>
        </QuickViewContext.Provider>
    );
};

QuickViewComponent.defaultProps = {
    queryparams: {},
    parentAccessor: null,
    extraProps: null,
    uuid: null,
    getTitle: undefined,
    getResourceArguments: undefined,
    resourceArguments: [],
    icon: null,
};

export default QuickViewComponent;
